import r2wc from "@r2wc/react-to-web-component";
import React from "react";

type Shadow = "open" | "closed";

interface ShadowOptions {
	shadow?: Shadow;
}

type WebComponentOptions = ShadowOptions & { jsonProp?: boolean };

const tagNamePrefix = "xtc";
const jsonProp = {
	props: {
		jsonProp: "json" as const,
	},
};

/**
 * Defines a custom element with the given tag name and component.
 */
function defineCustomElement(tagName: string, webComponent: CustomElementConstructor) {
	const prefixedTagName = `${tagNamePrefix}-${tagName}`;
	if (customElements.get(prefixedTagName) === undefined) {
		customElements.define(prefixedTagName, webComponent);
	}
}

function componentWithJsonProp<T extends object>(
	component: React.ComponentType<T>
): React.FC<{ jsonProp: T }> {
	const webComponent: React.FC<{ jsonProp: T }> = ({ jsonProp }) => {
		return React.createElement(component, { ...jsonProp });
	};
	return webComponent;
}

/**
 * Registers a React component as a Web Component.
 *
 * @returns The original React component.
 */
export function webComponent<T extends object>(
	tagName: string,
	component: React.ComponentType<T>,
	options: WebComponentOptions = {}
): React.ComponentType<T> {
	const { shadow } = options;
	const withJsonProp = options.jsonProp === true;
	const webComponentConstructor = withJsonProp
		? r2wc(componentWithJsonProp(component), { ...jsonProp, shadow })
		: r2wc(component, { shadow });
	defineCustomElement(tagName, webComponentConstructor);

	return component;
}

/**
 * Registers a React component as a Web Component.
 *
 * @returns The original React component.
 */
export function webComponentWithJsonProp<T extends object>(
	tagName: string,
	component: React.ComponentType<T>,
	options: ShadowOptions = {}
): React.ComponentType<T> {
	const { shadow } = options;
	const r2wcOptions = { ...jsonProp, shadow };
	const webComponentConstructor = r2wc(componentWithJsonProp(component), r2wcOptions);
	defineCustomElement(tagName, webComponentConstructor);

	return component;
}
